import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Download, FileText, Loader2 } from "lucide-react";
import { API } from "../config";

const formatSize = (b) => (b == null ? "" : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const DocRow = ({ doc, onDownload, busy }) => (
  <li className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-800 last:border-0" data-testid={`private-doc-${doc.id}`}>
    <div className="flex items-center gap-3 min-w-0">
      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-sm text-white truncate">{doc.title}</p>
        <p className="text-xs text-gray-500 truncate">{doc.filename} {doc.available ? `· ${formatSize(doc.size_bytes)}` : "· non disponibile"}</p>
      </div>
    </div>
    <button
      onClick={() => onDownload(doc)}
      disabled={!doc.available || busy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-gray-800 text-gray-200 hover:bg-lojack-red hover:text-white transition-colors disabled:opacity-40"
      data-testid={`private-doc-download-${doc.id}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      Scarica
    </button>
  </li>
);

export default function PrivateDocsPage() {
  const [docs, setDocs] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/private-docs`)
      .then((r) => setDocs(r.data.documents))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); else toast.error("Impossibile caricare i documenti"); setDocs([]); });
  }, []);

  const download = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await axios.get(`${API}/admin/private-docs/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    } finally {
      setBusyId(null);
    }
  };

  const categories = [...new Set((docs || []).map((d) => d.category))];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="private-docs-page">
      <div className="flex items-center gap-3 mb-2">
        <Lock className="w-6 h-6 text-lojack-red" />
        <h1 className="text-2xl font-semibold text-white">Documenti riservati</h1>
      </div>
      <p className="text-sm text-gray-500 mb-8">Visibili solo al tuo account. Brief, proposte, preventivi e note preparati per LoJack.</p>
      {docs === null && <Loader2 className="w-5 h-5 animate-spin text-gray-500" data-testid="private-docs-loading" />}
      {denied && <p className="text-sm text-gray-400" data-testid="private-docs-denied">Accesso riservato.</p>}
      {categories.map((cat) => (
        <section key={cat} className="mb-8" data-testid={`private-docs-category-${cat.toLowerCase().replace(/\s/g, "-")}`}>
          <h2 className="text-sm uppercase tracking-wide text-gray-400 mb-2">{cat}</h2>
          <ul className="bg-[#1A1A1A] border border-gray-800 rounded-xl">
            {docs.filter((d) => d.category === cat).map((d) => (
              <DocRow key={d.id} doc={d} onDownload={download} busy={busyId === d.id} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
